import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Button, Stack, Typography } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SettingsInputHdmiIcon from "@mui/icons-material/SettingsInputHdmi";
import { COLORS } from "./constants";
import { barePanelSx, primaryButtonSx, secondaryButtonSx, sectionSubtleSx, sectionTitleSx } from "./styles";
import { createTalkback } from "./talkCapture";
import { ReporterCard } from "./ReporterCard";
import { ProgrammeSetupDialog, ReporterSetupDialog } from "./ReporterSetupDialog";

/**
 * The account's ONE programme input from vMix (a bus with the studio and no
 * field reporter), as one status line. Its fields open in ProgrammeSetupDialog.
 */
function ProgrammeLine({ status, onSetup }) {
  const tone = status?.connected ? COLORS.green : status?.note === "no-audio" ? COLORS.amber : status?.running ? COLORS.amber : COLORS.muted;
  const text = status?.connected ? "connected"
    : status?.note === "no-audio" ? "connected, but no audio: pick a bus for that output"
    : status?.running ? "waiting for vMix"
    : "starts with the first live reporter";
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between"
      sx={{ mb: 1.25, px: 1.5, py: 1, borderRadius: "12px", border: `1px dashed ${COLORS.borderStrong}` }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="span" sx={{ color: COLORS.text, fontSize: 14, fontWeight: 700, mr: 1 }}>Programme from vMix</Typography>
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, verticalAlign: "middle" }}>
          <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: tone, flexShrink: 0 }} />
          <Typography component="span" sx={{ color: tone, fontSize: 12.5, fontWeight: 600 }}>{text}</Typography>
        </Box>
      </Box>
      <Button size="small" onClick={onSetup} aria-haspopup="dialog" sx={{ ...secondaryButtonSx, alignSelf: "center", flexShrink: 0 }}>
        vMix setup
      </Button>
    </Stack>
  );
}

// The first-time guide: three ticks, gone for good once all three were done
// (or the control room hides it). Browser storage only remembers that choice.
const GUIDE_KEY = "fennira.reportersGuideDone";
const readGuideDone = () => { try { return localStorage.getItem(GUIDE_KEY) === "1"; } catch { return false; } };
const saveGuideDone = () => { try { localStorage.setItem(GUIDE_KEY, "1"); } catch { /* private window */ } };

function Guide({ steps, onHide }) {
  return (
    <Box sx={{ mb: 1.5, p: 1.5, borderRadius: "12px", bgcolor: "rgba(43,189,175,0.06)", border: "1px solid rgba(43,189,175,0.28)" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        <Typography sx={{ color: COLORS.text, fontSize: 13.5, fontWeight: 700 }}>Getting started</Typography>
        <Button size="small" onClick={onHide} sx={{ color: COLORS.muted, textTransform: "none", minWidth: 0, py: 0 }}>Hide</Button>
      </Stack>
      <Stack component="ol" spacing={0.6} sx={{ m: 0, p: 0, listStyle: "none" }}>
        {steps.map((s, i) => (
          <Stack component="li" key={i} direction="row" spacing={1} alignItems="flex-start">
            {s.done
              ? <CheckCircleIcon sx={{ fontSize: 18, color: COLORS.green, mt: "1px" }} />
              : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: COLORS.muted, mt: "1px" }} />}
            <Typography sx={{ color: s.done ? COLORS.muted : COLORS.textSoft, fontSize: 13, lineHeight: 1.5 }}>
              <strong>{i + 1}.</strong> {s.text}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Field reporters (backend-/PLAN.md): the control room's view of its SRT
// channels — one card per reporter, at most `maxLive` live at once, the others
// on standby with their QR code still valid.
//
// One talkback microphone for the whole page (one permission prompt): a card's
// "Hold to talk" sends it to that reporter, "Talk to all" to every live one.
// ─────────────────────────────────────────────────────────────────────────────

const POLL_MS = 3000;

export function FieldReportersPanel({ axiosConfig, showMessage, copiedField, copyToClipboard }) {
  const [data, setData] = useState(null);
  const [configs, setConfigs] = useState({});      // channel → addresses (after the passkey re-check)
  const [busy, setBusy] = useState({});            // channel → an action in flight

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get("/srt/channels", axiosConfig);
      if (res.data?.success) setData(res.data);
    } catch { /* next poll */ }
  }, [axiosConfig]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const act = async (n, fn) => {
    setBusy((b) => ({ ...b, [n]: true }));
    try { await fn(); } catch (err) {
      showMessage?.(err.response?.data?.error || err.message || "Something went wrong", err.response?.status === 409 ? "warning" : "error");
    } finally {
      setBusy((b) => ({ ...b, [n]: false }));
      refresh();
    }
  };

  const loadConfig = (n) => act(n, async () => {
    const res = await axios.get(`/srt/channels/${n}/config`, axiosConfig);
    setConfigs((c) => ({ ...c, [n]: res.data }));
  });
  const goLive = (n) => act(n, () => axios.post(`/srt/channels/${n}/live`, {}, axiosConfig));
  const standby = (n) => act(n, async () => {
    if (talkRef.current.target === n) stopTalking();
    await axios.post(`/srt/channels/${n}/standby`, {}, axiosConfig);
  });
  const renew = (n) => act(n, async () => {
    const res = await axios.post(`/srt/channels/${n}/renew`, {}, axiosConfig);
    setConfigs((c) => ({ ...c, [n]: res.data }));
    showMessage?.("New code made. Send the reporter the new QR code and update this vMix input.", "success");
  });
  const patch = (n, body) => act(n, () => axios.patch(`/srt/channels/${n}`, body, axiosConfig));

  // The setup windows: one reporter's (channel number) or the programme's.
  const [setupFor, setSetupFor] = useState(null);
  const [programmeOpen, setProgrammeOpen] = useState(false);
  const openSetup = (n) => {
    setSetupFor(n);
    if (!configs[n] && !busy[n]) loadConfig(n);
  };
  const openProgramme = () => {
    setProgrammeOpen(true);
    if (!programmeConfig && !busy.programme) loadProgramme();
  };

  const [programmeConfig, setProgrammeConfig] = useState(null);
  const loadProgramme = () => act("programme", async () => {
    const res = await axios.get("/srt/programme", axiosConfig);
    setProgrammeConfig(res.data);
  });
  const renewProgramme = () => act("programme", async () => {
    const res = await axios.post("/srt/programme/renew", {}, axiosConfig);
    setProgrammeConfig(res.data);
    showMessage?.("New code for the programme input. Update vMix's SRT output.", "success");
  });

  // ── Talkback ──
  const [talkState, setTalkState] = useState("off");
  const [talkMessage, setTalkMessage] = useState("");
  const [talkTarget, setTalkTarget] = useState(null);   // channel number, "all", or null
  const meterRef = useRef(null);
  const tbRef = useRef(null);
  const talkRef = useRef({ target: null });

  useEffect(() => {
    const tb = createTalkback({
      onState: (s, msg) => { setTalkState(s); setTalkMessage(msg || ""); },
      onLevel: (peak) => {
        if (meterRef.current) meterRef.current.style.width = `${Math.min(100, Math.round(Math.sqrt(peak) * 100))}%`;
      }
    });
    tbRef.current = tb;
    return () => tb.disarm();
  }, []);

  const armed = talkState !== "off" && talkState !== "error";
  const ready = talkState === "ready";

  const startTalking = (target) => {
    if (!ready) return;
    tbRef.current.setTarget(target);
    tbRef.current.setTalking(true);
    talkRef.current.target = target;
    setTalkTarget(target);
  };
  const stopTalking = useCallback(() => {
    tbRef.current?.setTalking(false);
    talkRef.current.target = null;
    setTalkTarget(null);
  }, []);

  // Esc always stops talking, wherever the focus is.
  useEffect(() => {
    if (talkTarget == null) return undefined;
    const onKey = (e) => { if (e.key === "Escape") stopTalking(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [talkTarget, stopTalking]);

  const holdAll = {
    onPointerDown: (e) => { e.currentTarget.setPointerCapture?.(e.pointerId); startTalking("all"); },
    onPointerUp: stopTalking, onPointerCancel: stopTalking, onLostPointerCapture: stopTalking,
    onKeyDown: (e) => { if ((e.key === " " || e.key === "Enter") && !e.repeat) { e.preventDefault(); startTalking("all"); } },
    onKeyUp: (e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); stopTalking(); } },
    onContextMenu: (e) => e.preventDefault()
  };

  const channels = data?.channels || [];
  const liveCount = data?.liveCount ?? 0;
  const maxLive = data?.maxLive ?? 3;
  const anyLive = liveCount > 0;

  // Guide ticks stay ticked once seen, so a reporter going to standby doesn't undo them.
  const [guideDone, setGuideDone] = useState(readGuideDone);
  const [seen, setSeen] = useState({ programme: false, live: false, camera: false });
  useEffect(() => {
    if (!data) return;
    setSeen((s) => ({
      programme: s.programme || !!data.programme?.connected,
      live:      s.live || data.liveCount > 0,
      camera:    s.camera || data.channels.some((c) => c.cameraConnected)
    }));
  }, [data]);
  useEffect(() => {
    if (!guideDone && seen.programme && seen.live && seen.camera) { saveGuideDone(); setGuideDone(true); }
  }, [seen, guideDone]);
  const guideSteps = [
    { done: seen.programme, text: "Set up the vMix programme output once (vMix setup, above the reporters)." },
    { done: seen.live, text: "Press Go live on a reporter." },
    { done: seen.camera, text: "Open ⚙ on that reporter and let them scan the QR code with the Fennira Broadcast app." }
  ];
  const setupChannel = setupFor != null ? channels.find((c) => c.channel === setupFor) : null;

  return (
    <Box sx={barePanelSx}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} mb={2}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{
            width: 34, height: 34, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: anyLive ? "rgba(61,214,140,0.12)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${anyLive ? "rgba(61,214,140,0.4)" : COLORS.border}`
          }}>
            <SettingsInputHdmiIcon sx={{ fontSize: 18, color: anyLive ? COLORS.green : COLORS.muted }} />
          </Box>
          <Box>
            <Typography sx={sectionTitleSx}>Field reporters</Typography>
            <Typography sx={sectionSubtleSx}>
              {data ? `${liveCount} of ${maxLive} live · ${data.total} reporters` : "Loading…"}
            </Typography>
          </Box>
        </Stack>

        {/* The talkback microphone, shared by every card. */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {!armed ? (
            <Button size="small" variant="contained" startIcon={<MicIcon />} onClick={() => tbRef.current?.arm()} sx={primaryButtonSx}>
              Turn on talkback mic
            </Button>
          ) : (
            <>
              <Box sx={{ width: 90, height: 6, borderRadius: 3, bgcolor: "rgba(0,0,0,0.35)", overflow: "hidden" }} aria-hidden="true">
                <Box ref={meterRef} sx={{ width: "0%", height: "100%", bgcolor: talkTarget != null ? COLORS.amber : COLORS.muted, transition: "width 60ms linear" }} />
              </Box>
              <Button size="small" variant="contained" startIcon={<CampaignIcon />} disabled={!ready || !anyLive}
                aria-pressed={talkTarget === "all"} {...holdAll}
                sx={{ ...primaryButtonSx, userSelect: "none", touchAction: "none",
                  ...(talkTarget === "all" ? { bgcolor: `${COLORS.amber} !important`, color: `${COLORS.onAccent} !important` } : {}) }}>
                {talkTarget === "all" ? "Talking to all…" : "Hold: talk to all"}
              </Button>
              <Button size="small" startIcon={<MicOffIcon />} onClick={() => { stopTalking(); tbRef.current?.disarm(); }} sx={secondaryButtonSx}>
                Mic off
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      {(talkState === "error" || talkState === "connecting") && (
        <Typography sx={{ mb: 1.5, color: talkState === "error" ? "#E8A0A0" : COLORS.muted, fontSize: 12.5 }}>
          {talkState === "error" ? talkMessage : "Talkback: connecting…"}
        </Typography>
      )}

      {data && !guideDone && <Guide steps={guideSteps} onHide={() => { saveGuideDone(); setGuideDone(true); }} />}

      {data?.programme && <ProgrammeLine status={data.programme} onSetup={openProgramme} />}

      <Stack spacing={0.75}>
        {channels.map((ch) => (
          <ReporterCard key={ch.channel}
            channel={ch} busy={!!busy[ch.channel]}
            canGoLive={liveCount < maxLive}
            talk={{ ready, active: talkTarget != null, target: talkTarget, start: startTalking, stop: stopTalking }}
            onGoLive={goLive} onStandby={standby} onPatch={patch} onSetup={openSetup}
          />
        ))}
      </Stack>

      <Typography sx={{ mt: 1.5, color: COLORS.muted, fontSize: 12, lineHeight: 1.6 }}>
        Standby keeps a reporter's QR code and vMix input valid: press Go live and their phone reconnects by itself.
        A live reporter with no camera goes to standby on its own (30 min, or 3 h after the last use).
      </Typography>

      <ReporterSetupDialog open={setupChannel != null} onClose={() => setSetupFor(null)}
        channel={setupChannel} config={setupChannel ? configs[setupChannel.channel] || null : null}
        busy={setupChannel ? !!busy[setupChannel.channel] : false}
        onLoadConfig={loadConfig} onRenew={renew} onPatch={patch}
        copiedField={copiedField} copyToClipboard={copyToClipboard} />
      <ProgrammeSetupDialog open={programmeOpen} onClose={() => setProgrammeOpen(false)}
        config={programmeConfig} busy={!!busy.programme} onLoad={loadProgramme} onRenew={renewProgramme}
        copiedField={copiedField} copyToClipboard={copyToClipboard} />
    </Box>
  );
}
